import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function DroppableColumn({ id, title, count, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-xl border min-h-[220px] flex flex-col transition-colors ${
        isOver ? 'border-[#d42b92]/70 bg-[#d42b92]/10' : 'border-white/10 bg-black/25'
      }`}
    >
      <div className="px-3 py-2.5 border-b border-white/5 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.14em] text-white/45">{title}</span>
        <span className="text-[10px] text-white/30 tabular-nums">{count}</span>
      </div>
      <div className="p-2 space-y-2 flex-1">{children}</div>
    </div>
  );
}

function SortableCard({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`rounded-lg border border-white/10 bg-[#0f0f12] px-3 py-2.5 cursor-grab active:cursor-grabbing touch-none ${
        disabled ? 'cursor-default' : 'hover:border-[#d42b92]/40'
      }`}
    >
      {children}
    </div>
  );
}

/**
 * columns: [{ id, title }]
 * items: [{ id, columnId, ... }]
 * renderCard(item, { isOverlay })
 * onMove(itemId, fromColumnId, toColumnId) => Promise|void
 */
export default function KanbanBoard({
  columns,
  items,
  renderCard,
  onMove,
  disabled = false,
  className = '',
}) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const itemsByColumn = useMemo(() => {
    const map = Object.fromEntries(columns.map((c) => [c.id, []]));
    for (const item of items) {
      if (!map[item.columnId]) map[item.columnId] = [];
      map[item.columnId].push(item);
    }
    return map;
  }, [columns, items]);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const activeItem = activeId ? itemMap[activeId] : null;

  const findColumnOf = (id) => {
    if (columns.some((c) => c.id === id)) return id;
    return itemMap[id]?.columnId || null;
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || disabled) return;
    const from = findColumnOf(active.id);
    const to = findColumnOf(over.id);
    if (!from || !to || from === to) return;
    await onMove?.(String(active.id), from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(String(e.active.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div className={`grid gap-3 ${className || 'md:grid-cols-4 xl:grid-cols-5'}`}>
        {columns.map((col) => {
          const colItems = itemsByColumn[col.id] || [];
          return (
            <DroppableColumn key={col.id} id={col.id} title={col.title} count={colItems.length}>
              <SortableContext
                items={colItems.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                {colItems.map((item) => (
                  <SortableCard key={item.id} id={item.id} disabled={disabled}>
                    {renderCard(item, { isOverlay: false })}
                  </SortableCard>
                ))}
              </SortableContext>
              {colItems.length === 0 && (
                <div className="text-[11px] text-white/25 px-1 py-6 text-center border border-dashed border-white/10 rounded-lg">
                  Drop here
                </div>
              )}
            </DroppableColumn>
          );
        })}
      </div>
      <DragOverlay>
        {activeItem ? (
          <div className="rounded-lg border border-[#d42b92]/60 bg-[#141418] px-3 py-2.5 shadow-xl shadow-black/40 scale-[1.02]">
            {renderCard(activeItem, { isOverlay: true })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export function statusChip(status) {
  return (
    <span className="text-[10px] uppercase tracking-wide text-[#ff8ec5]">
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}
